import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { bool, func, number, shape, string } from 'prop-types';
import * as R from 'ramda';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

// utils
import { useIntersect, useUserRolePermissions } from 'hooks';

// components
import SupplierAuthCardBody from './card-components/SupplierAuthCardBody';
import SupplierAuthCardHeader from './card-components/SupplierAuthCardHeader';
import SupplierAuthActions from './card-components/SupplierAuthActions.connector';

const SupplierAuthCard = ({ authorizationData, cardHeight, index, setCardHeight }) => {
  // any function ran here will be evaluated on every rerender, regardless if hideCard is true or false
  const { entry, setNode } = useIntersect({ rootMargin: '300px' });
  const node = useRef();
  const { viewBy, viewById } = useParams();
  const { isProductLoadingManage } = useUserRolePermissions();

  useEffect(() => {
    setNode(node.current);
  }, [setNode]);

  // since the first 10 cards are always rendered, we can pull the card height off the first one right away
  // allows us to make future changes to the cards without worrying about keeping the card heights
  useEffect(() => {
    if (index === 0) setCardHeight(node.current.getBoundingClientRect().height);
  }, [index, setCardHeight]);

  // isIntersecting fixes a mobile bug that causes cards just outside the parent to not render
  const { intersectionRatio, isIntersecting } = entry;
  // show the first 10 cards on initial render, when intersectionRatio is undefined
  // otherwise, only show if intersectionRatio > 0
  // keeps from showing blank cards on initial load
  const hideCard =
    (R.isNil(intersectionRatio) && index > 10) || (intersectionRatio === 0 && !isIntersecting);

  const {
    active,
    endDate,
    exchange,
    lastUsed,
    limitMet,
    limitType,
    limitVolume,
    loadableVolume,
    loadableVolumeUnitOfMeasure,
    location,
    product,
    productAuthorizationId,
    remainingVolume,
    supplier,
    unlimited,
  } = authorizationData;
  const getCardHeaderInfo = R.cond([
    [
      R.equals('supplier'),
      R.always({
        subtitle: location.nameShort,
        title: product?.nameShort,
      }),
    ],
    [
      R.equals('product'),
      R.always({
        subtitle: location.nameShort,
        title: supplier.nameShort,
      }),
    ],
    [
      R.equals('location'),
      R.always({
        subtitle: supplier.nameShort,
        title: product?.nameShort,
      }),
    ],
  ]);
  const cardHeaderInfo = getCardHeaderInfo(viewBy);
  const cardBodyData = {
    active,
    endDate,
    exchange,
    lastUsed,
    limitMet,
    limitType,
    limitVolume,
    loadableVolume,
    loadableVolumeUnitOfMeasure,
    remainingVolume,
    unlimited,
  };

  const params = {
    locationName: location.nameShort,
    productAuthorizationId,
    productName: viewBy === 'product' ? viewById : product?.nameShort,
    supplierName: supplier.nameShort,
  };

  return (
    <Card
      ref={node}
      sx={{
        height: hideCard && cardHeight ? cardHeight : 'auto',
        minHeight: 234,
      }}
      variant="loading"
    >
      <CardContent variant="loading">
        {!hideCard && (
          <>
            <SupplierAuthCardHeader
              active={active}
              proprietary={product?.proprietary}
              subtitle={cardHeaderInfo?.subtitle}
              title={cardHeaderInfo?.title}
              {...params}
            />
            <SupplierAuthCardBody cardBodyData={cardBodyData} {...params} />
            {isProductLoadingManage ? (
              <SupplierAuthActions active={active} limitType={limitType} {...params} />
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
};

SupplierAuthCard.propTypes = {
  authorizationData: shape({
    active: bool.isRequired,
    endDate: string,
    exchange: shape({}),
    lastUsed: string,
    limitMet: bool,
    limitType: string,
    limitVolume: number,
    loadableVolume: number,
    loadableVolumeUnitOfMeasure: string,
    location: shape({
      nameShort: string.isRequired,
    }).isRequired,
    product: shape({
      nameShort: string,
      proprietary: bool,
    }),
    supplier: shape({
      nameShort: string.isRequired,
    }).isRequired,
    unlimited: bool.isRequired,
  }).isRequired,
  cardHeight: number.isRequired,
  index: number.isRequired,
  setCardHeight: func.isRequired,
};

export default SupplierAuthCard;
